import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO, buildVehicleLD } from "../components/SEO";
import {
  Phone, MapPin, Fuel, Users, Settings,
  CheckCircle, ChevronRight, Heart, Share2, Star, Loader2,
  ZoomIn, ZoomOut, X, ArrowLeftRight, ThumbsUp, ChevronLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import CarCard from "../components/CarCard";
import odooApi, { buildPhoneHref, buildWhatsAppUrl } from "../services/odooApi";
import { useAuth } from "../contexts/AuthContext";
import { useAuthDialog } from "../contexts/AuthDialogContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useCompare } from "../contexts/CompareContext";
import CarImage from "../components/CarImage";
import WhatsAppIcon from "../components/WhatsAppIcon";

// Image Zoom Modal Component
const ImageZoomModal = ({ images, currentIndex, isOpen, onClose, onNavigate }) => {
  const [scale, setScale] = useState(1);
  
  if (!isOpen) return null;
  
  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.5, 1));
  
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="image-zoom-modal">
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
      
      {/* Zoom controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
        <button onClick={handleZoomOut} className="text-white hover:text-[#D4AF37] transition-colors" disabled={scale <= 1}>
          <ZoomOut className="w-5 h-5" />
        </button>
        <span className="text-white text-sm min-w-[50px] text-center">{Math.round(scale * 100)}%</span>
        <button onClick={handleZoomIn} className="text-white hover:text-[#D4AF37] transition-colors" disabled={scale >= 3}>
          <ZoomIn className="w-5 h-5" />
        </button>
      </div>
      
      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button 
            onClick={() => { onNavigate(-1); setScale(1); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button 
            onClick={() => { onNavigate(1); setScale(1); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      
      {/* Image */}
      <div className="overflow-auto max-w-full max-h-full">
        <CarImage
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="transition-transform duration-200"
          style={{ transform: `scale(${scale})`, cursor: scale > 1 ? 'move' : 'zoom-in' }}
          onClick={() => scale === 1 && handleZoomIn()}
        />
      </div>
      
      {/* Thumbnails */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((img, idx) => (
          <button 
            key={idx}
            onClick={() => { onNavigate(idx - currentIndex); setScale(1); }}
            className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${idx === currentIndex ? 'border-[#D4AF37]' : 'border-transparent opacity-60 hover:opacity-100'}`}
          >
            <CarImage src={img} alt="" />
          </button>
        ))}
      </div>
    </div>
  );
};

// Review Component
const ReviewCard = ({ review, onMarkHelpful, helpfulLoadingId }) => {
  const reviewerName = review.user?.name || review.name || "Guest";
  const helpfulCount = review.helpfulCount || review.helpful || 0;

  return (
  <div className="border-b dark:border-gray-700 pb-6 mb-6 last:border-0 last:mb-0 last:pb-0">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
        <span className="text-[#D4AF37] font-bold">{reviewerName.substring(0, 2).toUpperCase()}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">{reviewerName}</h4>
            <p className="text-sm text-gray-500">{new Date(review.date).toLocaleDateString()}</p>
          </div>
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-300'}`} />
            ))}
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-300">{review.comment}</p>
        <div className="flex items-center gap-4 mt-3">
          <button
            onClick={() => onMarkHelpful(review.id)}
            disabled={helpfulLoadingId === review.id}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#D4AF37] transition-colors disabled:opacity-50"
          >
            <ThumbsUp className="w-4 h-4" />
            <span>Helpful ({helpfulCount})</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};

export const CarDetailPage = () => {
  const { id } = useParams();
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  const { isAuthenticated, isFavorite, addToFavorites, removeFromFavorites } = useAuth();
  const { openAuth } = useAuthDialog();
  const { compareCars, addToCompare, removeFromCompare, clearCompare, isInCompare } = useCompare();
  
  const [car, setCar] = useState(null);
  const [similarCars, setSimilarCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  // Inquiry flow is hidden for the current release. Keep state here for later restore.
  // const [inquiryForm, setInquiryForm] = useState({ name: "", email: "", phone: "", message: "" });
  
  // New states for enhanced features
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [newReview, setNewReview] = useState({ rating: 5, comment: "" });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [helpfulLoadingId, setHelpfulLoadingId] = useState(null);

  useEffect(() => {
    const fetchCar = async () => {
      setLoading(true);
      setReviewsLoading(true);
      try {
        const carData = await odooApi.getCar(id);
        setCar(carData);

        const reviewData = await odooApi.getCarReviews(id);
        setReviews(reviewData.reviews || []);
        setAverageRating(Number(reviewData.averageRating || 0));
        setTotalReviews(reviewData.totalReviews || 0);

        // Fetch similar cars from same category
        if (carData.categorySlug) {
          const catCars = await odooApi.getCategoryCars(carData.categorySlug);
          setSimilarCars(catCars.filter(c => c.id !== carData.id).slice(0, 4));
        }
      } catch (err) {
        console.error("Failed to load car:", err);
      } finally {
        setLoading(false);
        setReviewsLoading(false);
      }
    };
    fetchCar();
  }, [id]);

  useEffect(() => {
    if (car?.id) {
      odooApi.trackCarView(car.id, `/cars/${car.id}`);
    }
  }, [car?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t("carDetail.notFound", "Car Not Found")}</h2>
          <Link to="/cars" className="text-[#D4AF37] hover:underline">{t("carDetail.browseAll", "Browse all cars")}</Link>
        </div>
      </div>
    );
  }

  const images = car.gallery?.length > 0
    ? car.gallery
    : [car.image].filter(Boolean);

  const whatsappNumber = car.supplierWhatsApp || car.supplierPhone;
  const whatsappMessage = `Hi! I'm interested in renting the ${car.brand} ${car.model} (${car.year}). Please provide more details and availability.`;
  const whatsappUrl = buildWhatsAppUrl(whatsappNumber, whatsappMessage, '');
  const callUrl = buildPhoneHref(car.supplierPhone, '');
  const currentCarCompared = isInCompare(car.id);

  const handleContactClick = (type) => {
    odooApi.trackClick({
      click_type: type,
      car_id: car.id,
      supplier_id: car.supplierId,
    });
  };

  // Inquiry flow is hidden for the current release. Keep handler here for later restore.
  // const handleInquirySubmit = async (e) => {
  //   e.preventDefault();
  //   try {
  //     await odooApi.createLead({
  //       name: inquiryForm.name,
  //       email: inquiryForm.email,
  //       phone: inquiryForm.phone,
  //       message: inquiryForm.message,
  //       car_id: car.id,
  //       source: 'web_detail',
  //     });
  //     toast.success(t("carDetail.inquirySent", "Inquiry sent successfully! We'll contact you soon."));
  //     setInquiryForm({ name: "", email: "", phone: "", message: "" });
  //   } catch {
  //     toast.success(t("carDetail.inquirySent", "Inquiry sent successfully! We'll contact you soon."));
  //     setInquiryForm({ name: "", email: "", phone: "", message: "" });
  //   }
  // };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: `${car.brand} ${car.model} - Velaris Royal`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success(t("common.linkCopied", "Link copied to clipboard!"));
    }
  };

  const handleCompare = (compareCar) => {
    const alreadyCompared = isInCompare(compareCar.id);

    if (alreadyCompared) {
      removeFromCompare(compareCar.id);
      toast.success(t("compare.removedToast", "Car removed from comparison."));
      return;
    }

    const result = addToCompare(compareCar);
    if (!result.success) {
      toast.error(
        result.reason === "limit"
          ? t("compare.limitReached", "You can compare up to 4 cars at once.")
          : t("compare.alreadyAdded", "This car is already in your comparison tray."),
      );
      return;
    }

    toast.success(
      compareCars.length === 0
        ? t("compare.firstAdded", "Car added. Pick one more to open comparison.")
        : t("compare.addedToast", "Car added to comparison."),
    );
  };

  const handleImageNavigate = (delta) => {
    setSelectedImage(prev => (prev + delta + images.length) % images.length);
  };

  const handleFavoriteToggle = async () => {
    if (!car) {
      return;
    }

    if (!isAuthenticated) {
      openAuth("login");
      toast.error(t("auth.signInRequired", "Please sign in to save favorites."));
      return;
    }

    const wasFavorite = isFavorite(car.id);
    const result = wasFavorite
      ? await removeFromFavorites(car.id)
      : await addToFavorites(car.id);

    if (!result.success) {
      toast.error(result.error || t("favorites.updateError", "Failed to update favorites."));
      return;
    }

    toast.success(
      wasFavorite
        ? t("favorites.removed", "Removed from favorites.")
        : t("favorites.added", "Added to favorites."),
    );
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      openAuth("login");
      toast.error(t("auth.signInRequired", "Please sign in to leave a review."));
      return;
    }

    if (!newReview.comment.trim()) {
      toast.error(t("carDetail.shareExperience", "Share your experience before submitting."));
      return;
    }

    setReviewSubmitting(true);
    try {
      const result = await odooApi.createReview(car.id, {
        rating: newReview.rating,
        comment: newReview.comment.trim(),
      });

      if (!result.success || !result.review) {
        toast.error(result.error || t("carDetail.reviewError", "Failed to submit your review."));
        return;
      }

      const nextReviews = [result.review, ...reviews];
      const totalRating = nextReviews.reduce((sum, review) => sum + (review.rating || 0), 0);
      setReviews(nextReviews);
      setTotalReviews(nextReviews.length);
      setAverageRating(nextReviews.length ? totalRating / nextReviews.length : 0);
      setNewReview({ rating: 5, comment: "" });
      setShowReviewForm(false);
      toast.success(t("carDetail.reviewSubmitted", "Review submitted successfully!"));
    } catch (error) {
      toast.error(error.message || t("carDetail.reviewError", "Failed to submit your review."));
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleReviewHelpful = async (reviewId) => {
    setHelpfulLoadingId(reviewId);
    try {
      const result = await odooApi.markReviewHelpful(reviewId, true);
      if (result.success) {
        setReviews((previousReviews) =>
          previousReviews.map((review) =>
            review.id === reviewId
              ? {
                  ...review,
                  helpfulCount: (review.helpfulCount || review.helpful || 0) + 1,
                }
              : review,
          ),
        );
      }
    } finally {
      setHelpfulLoadingId(null);
    }
  };

  const displayedAverageRating = totalReviews > 0 ? averageRating.toFixed(1) : "0.0";

  const included = [
    t("carDetail.insurance", "Comprehensive Insurance"),
    t("carDetail.roadside", "24/7 Roadside Assistance"),
    t("carDetail.delivery", "Free Delivery & Pickup"),
    t("carDetail.mileage", "200km daily mileage"),
  ];

  const requirements = [
    t("carDetail.req1", "Valid UAE driving license or International Driving Permit"),
    t("carDetail.req2", "Minimum age: 25 years"),
    `${t("carDetail.req3", "Security deposit")}: ${formatPrice(car.securityDeposit || 5000)}`,
    t("carDetail.req4", "Valid passport copy"),
    t("carDetail.req5", "Credit card authorization"),
  ];

  return (
    <>
      <SEO
        title={`${t("carDetail.rent", "Rent")} ${car.brand} ${car.model} ${car.year} ${t("carDetail.in", "in")} ${car.city}`}
        description={`${t("carDetail.rent", "Rent")} ${car.brand} ${car.model} ${t("carDetail.in", "in")} ${car.city} ${t("carDetail.from", "from")} ${formatPrice(car.pricePerDay)}/${t("common.day", "day")}. ${(car.description || 'Luxury car rental in UAE with best prices and instant booking').substring(0, 150)}`}
        keywords={`rent ${car.brand} ${car.model}, ${car.brand} rental ${car.city}, ${car.category} car rental dubai, luxury car hire uae`}
        image={car.image}
        type="product"
        structuredData={buildVehicleLD(car)}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Cars", url: "/cars" },
          { name: `${car.brand} ${car.model}` },
        ]}
      />

      {/* Image Zoom Modal */}
      <ImageZoomModal 
        images={images}
        currentIndex={selectedImage}
        isOpen={zoomModalOpen}
        onClose={() => setZoomModalOpen(false)}
        onNavigate={handleImageNavigate}
      />
      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="car-detail-page">
        <div className="bg-gray-50 dark:bg-gray-900 border-b dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-[#D4AF37]">{t("nav.home", "Home")}</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <Link to="/cars" className="text-gray-500 hover:text-[#D4AF37]">{t("nav.cars", "Cars")}</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <Link to={`/category/${car.categorySlug}`} className="text-gray-500 hover:text-[#D4AF37]">{car.category}</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900 dark:text-white">{car.brand} {car.model}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              {/* Main Image with Zoom */}
              <div 
                className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 mb-4 cursor-zoom-in group"
                onClick={() => setZoomModalOpen(true)}
              >
                <CarImage
                  src={images[selectedImage] || car.image}
                  alt={`${car.brand} ${car.model}`}
                  className="transition-transform group-hover:scale-105"
                  data-testid="main-image"
                />
                
                {/* Zoom indicator */}
                <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="w-4 h-4" />
                  {t("carDetail.clickToZoom", "Click to zoom")}
                </div>
                
                <div className="absolute top-4 right-4 flex gap-2">
                  <button onClick={(e) => { e.stopPropagation(); handleFavoriteToggle(); }} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isFavorite(car.id) ? "bg-red-500 text-white" : "bg-white/90 text-gray-600 hover:text-red-500"}`} data-testid="favorite-btn">
                    <Heart className={`w-5 h-5 ${isFavorite(car.id) ? "fill-current" : ""}`} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleShare(); }} className="w-10 h-10 rounded-full bg-white/90 text-gray-600 flex items-center justify-center hover:text-[#D4AF37] transition-colors" data-testid="share-btn">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="absolute top-4 left-4 bg-gold-gradient px-4 py-1.5 rounded-sm">
                  <span className="text-black font-semibold text-sm">{car.category}</span>
                </div>
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.slice(0, 4).map((image, index) => (
                    <button key={index} onClick={() => setSelectedImage(index)} className={`aspect-[4/3] rounded-lg overflow-hidden ${selectedImage === index ? "ring-2 ring-[#D4AF37]" : "opacity-60 hover:opacity-100"}`} data-testid={`thumbnail-${index}`}>
                      <CarImage
                        src={image}
                        alt={`${car.brand} ${car.model} ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Detail Tabs */}
              <div className="mt-8">
                <Tabs defaultValue="description" className="w-full">
                  <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0 gap-4 sm:gap-8 overflow-x-auto">
                    <TabsTrigger value="description" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] data-[state=active]:text-[#D4AF37] px-0 pb-4 whitespace-nowrap">{t("carDetail.description", "Description")}</TabsTrigger>
                    <TabsTrigger value="features" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] data-[state=active]:text-[#D4AF37] px-0 pb-4 whitespace-nowrap">{t("carDetail.features", "Features")}</TabsTrigger>
                    <TabsTrigger value="specs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] data-[state=active]:text-[#D4AF37] px-0 pb-4 whitespace-nowrap">{t("carDetail.specs", "Specifications")}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="description" className="pt-6">
                    <div className="text-gray-600 dark:text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: car.description || t("carDetail.noDescription", "No description available.") }} />
                    <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">{t("carDetail.whatsIncluded", "What's Included")}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {included.map((item, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="features" className="pt-6">
                    {car.features?.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {car.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-[#D4AF37]" />
                            <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">{t("carDetail.featuresComingSoon", "Features information coming soon.")}</p>
                    )}
                  </TabsContent>

                  <TabsContent value="specs" className="pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {car.engine && <div className="flex justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"><span className="text-gray-500">{t("carDetail.engine", "Engine")}</span><span className="font-semibold text-gray-900 dark:text-white">{car.engine}</span></div>}
                      {car.power && <div className="flex justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"><span className="text-gray-500">{t("carDetail.power", "Power")}</span><span className="font-semibold text-gray-900 dark:text-white">{car.power}</span></div>}
                      <div className="flex justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"><span className="text-gray-500">{t("carDetail.transmission", "Transmission")}</span><span className="font-semibold text-gray-900 dark:text-white">{car.transmission}</span></div>
                      <div className="flex justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"><span className="text-gray-500">{t("carDetail.fuelType", "Fuel Type")}</span><span className="font-semibold text-gray-900 dark:text-white">{car.fuelType}</span></div>
                      <div className="flex justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"><span className="text-gray-500">{t("carDetail.seats", "Seats")}</span><span className="font-semibold text-gray-900 dark:text-white">{car.seats}</span></div>
                      <div className="flex justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"><span className="text-gray-500">{t("carDetail.doors", "Doors")}</span><span className="font-semibold text-gray-900 dark:text-white">{car.doors}</span></div>
                    </div>
                  </TabsContent>

                </Tabs>
              </div>
            </div>

            {/* Booking Card */}
            <div className="lg:col-span-5">
              <div className="sticky top-24">
                <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-lg p-6 shadow-lg" data-testid="booking-card">
                  <h1 className="font-[var(--font-display)] text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {car.brand} {car.model}
                  </h1>
                  <p className="text-gray-500 mb-6">{car.year}</p>

                  <div className="grid grid-cols-4 gap-4 mb-6 pb-6 border-b dark:border-gray-700">
                    <div className="text-center"><Users className="w-5 h-5 mx-auto text-gray-400 mb-1" /><span className="text-sm text-gray-600 dark:text-gray-300">{car.seats} {t("common.seats", "Seats")}</span></div>
                    <div className="text-center"><Settings className="w-5 h-5 mx-auto text-gray-400 mb-1" /><span className="text-sm text-gray-600 dark:text-gray-300">{car.transmission}</span></div>
                    <div className="text-center"><Fuel className="w-5 h-5 mx-auto text-gray-400 mb-1" /><span className="text-sm text-gray-600 dark:text-gray-300">{car.fuelType}</span></div>
                    <div className="text-center"><MapPin className="w-5 h-5 mx-auto text-gray-400 mb-1" /><span className="text-sm text-gray-600 dark:text-gray-300">{car.city}</span></div>
                  </div>

                  <div className="mb-6">
                    {car.activeOffer && (
                      <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-200">
                        <strong>{car.activeOffer.title}</strong>
                      </div>
                    )}
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-4xl font-bold text-gold-gradient">{formatPrice(car.pricePerDay)}</span>
                      <span className="text-gray-500">/{t("common.day", "day")}</span>
                      {car.basePricePerDay > car.pricePerDay && (
                        <span className="text-sm text-gray-400 line-through">
                          {formatPrice(car.basePricePerDay)}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-xs text-gray-500 uppercase mb-1">{t("common.weekly", "Weekly")}</p>
                        <p className="font-semibold dark:text-white">{formatPrice(car.pricePerWeek)}</p>
                        {car.basePricePerWeek > car.pricePerWeek && (
                          <p className="text-xs text-gray-400 line-through">{formatPrice(car.basePricePerWeek)}</p>
                        )}
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-xs text-gray-500 uppercase mb-1">{t("common.monthly", "Monthly")}</p>
                        <p className="font-semibold dark:text-white">{formatPrice(car.pricePerMonth)}</p>
                        {car.basePricePerMonth > car.pricePerMonth && (
                          <p className="text-xs text-gray-400 line-through">{formatPrice(car.basePricePerMonth)}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 rounded-2xl border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-800 dark:bg-gray-800/60">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-900 dark:text-white mb-1 block">
                            {t("compare.title", "Compare Cars")}
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("compare.helper", "Add up to 4 cars and review pricing, specs, and supplier details side by side.")}
                          </p>
                        </div>
                        {compareCars.length > 0 && (
                          <button
                            type="button"
                            onClick={clearCompare}
                            className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 transition-colors hover:text-[#D4AF37]"
                          >
                            {t("compare.clear", "Clear comparison")}
                          </button>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleCompare(car)}
                        className={`justify-center border-[#D4AF37]/40 ${
                          currentCarCompared
                            ? "bg-[#D4AF37] text-black hover:bg-[#C5A028]"
                            : "text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                        }`}
                        data-testid="compare-current-car"
                      >
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        {currentCarCompared
                          ? t("compare.removeCurrent", "Remove current car")
                          : t("compare.addCurrent", "Add current car")}
                      </Button>

                      {similarCars.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {similarCars.slice(0, 3).map((similarCar) => {
                            const compared = isInCompare(similarCar.id);
                            return (
                              <button
                                key={similarCar.id}
                                type="button"
                                onClick={() => handleCompare(similarCar)}
                                className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors ${
                                  compared
                                    ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                                    : "border-gray-200 bg-white text-gray-700 hover:border-[#D4AF37] hover:text-[#D4AF37] dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                                }`}
                              >
                                <ArrowLeftRight className="h-4 w-4" />
                                {similarCar.brand} {similarCar.model}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {compareCars.length > 1 && (
                        <Link to="/compare" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:text-[#C5A028]">
                          {t("compare.open", "Open comparison")}
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {whatsappUrl !== '#' && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleContactClick('whatsapp')}
                        className="flex h-14 w-full items-center justify-center rounded-lg bg-[#25D366] text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#1da851] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/35 focus-visible:ring-offset-2 dark:ring-offset-gray-950"
                        data-testid="whatsapp-btn"
                        title={t("btn.whatsapp", "WhatsApp")}
                        aria-label={t("btn.whatsapp", "WhatsApp")}
                      >
                        <WhatsAppIcon className="h-5 w-5" />
                      </a>
                    )}
                    {callUrl !== '#' && (
                      <a
                        href={callUrl}
                        onClick={() => handleContactClick('call')}
                        className="flex h-14 w-full items-center justify-center rounded-lg bg-gray-900 text-white shadow-sm transition-colors hover:bg-gray-800 hover:shadow-md dark:bg-gray-700 dark:hover:bg-gray-600"
                        data-testid="call-btn"
                        title={t("btn.callNow", "Call Now")}
                        aria-label={t("btn.callNow", "Call Now")}
                      >
                        <Phone className="w-5 h-5" />
                      </a>
                    )}
                    {/* Inquiry CTA is hidden for the current release. Keep dialog here for later restore.
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full py-4 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black dark:bg-transparent" data-testid="inquiry-btn">
                          <Calendar className="w-5 h-5 mr-2" />{t("btn.sendInquiry", "Send Inquiry")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle className="font-[var(--font-display)] text-xl">{t("carDetail.inquiryFor", "Inquiry for")} {car.brand} {car.model}</DialogTitle></DialogHeader>
                        <form onSubmit={handleInquirySubmit} className="space-y-4 mt-4">
                          <div><label className="text-sm font-medium mb-1 block">{t("form.name", "Name")}</label><Input value={inquiryForm.name} onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })} required data-testid="inquiry-name" /></div>
                          <div><label className="text-sm font-medium mb-1 block">{t("form.email", "Email")}</label><Input type="email" value={inquiryForm.email} onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })} required data-testid="inquiry-email" /></div>
                          <div><label className="text-sm font-medium mb-1 block">{t("form.phone", "Phone")}</label><Input value={inquiryForm.phone} onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })} required data-testid="inquiry-phone" /></div>
                          <div><label className="text-sm font-medium mb-1 block">{t("form.message", "Message")}</label><Textarea value={inquiryForm.message} onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })} placeholder={t("carDetail.rentalRequirements", "Tell us about your rental requirements...")} rows={4} data-testid="inquiry-message" /></div>
                          <Button type="submit" className="w-full btn-gold text-black font-semibold" data-testid="inquiry-submit">{t("btn.submitInquiry", "Submit Inquiry")}</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                    */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similar Vehicles */}
          {similarCars.length > 0 && (
            <section className="mt-16 pt-16 border-t dark:border-gray-800">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
                  {isRTL ? (
                    <>
                      {t("carDetail.vehicles", "Vehicles")} <span className="text-gold-gradient">{t("carDetail.similar", "Similar")}</span>
                    </>
                  ) : (
                    <>
                      {t("carDetail.similar", "Similar")} <span className="text-gold-gradient">{t("carDetail.vehicles", "Vehicles")}</span>
                    </>
                  )}
                </h2>
                <Link to={`/category/${car.categorySlug}`} className="text-[#D4AF37] hover:text-[#C5A028] font-medium flex items-center gap-1">
                  {t("btn.viewAll", "View All")}<ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {similarCars.map((c) => (<CarCard key={c.id} car={c} />))}
              </div>
            </section>
          )}

          <section className="mt-16 pt-16 border-t dark:border-gray-800" data-testid="reviews-section">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-3 py-1 text-sm font-medium text-[#D4AF37]">
                    <span>{reviews.length}</span>
                    <span>{t("carDetail.reviews", "Reviews")}</span>
                  </div>
                  <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
                    {t("carDetail.reviews", "Reviews")}
                  </h2>
                </div>
                <Button
                  onClick={() => {
                    if (!isAuthenticated) {
                      openAuth("login");
                      toast.error(t("auth.signInRequired", "Please sign in to leave a review."));
                      return;
                    }
                    setShowReviewForm(!showReviewForm);
                  }}
                  className="btn-gold text-black"
                >
                  {t("carDetail.writeReview", "Write a Review")}
                </Button>
              </div>

              <div className="rounded-3xl border border-gray-100 bg-gray-50/80 p-6 dark:border-gray-800 dark:bg-gray-900/80 sm:p-8">
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-4 border-b pb-6 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl font-bold text-[#D4AF37]">{displayedAverageRating}</div>
                      <div>
                        <div className="mb-1 flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-5 h-5 ${i < Math.round(averageRating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-300'}`} />
                          ))}
                        </div>
                        <p className="text-sm text-gray-500">
                          {t("carDetail.basedOn", "Based on")} {totalReviews} {t("carDetail.reviewsCount", "reviews")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {showReviewForm && (
                    <form onSubmit={handleReviewSubmit} className="rounded-2xl bg-white p-6 dark:bg-gray-950">
                      <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">{t("carDetail.yourReview", "Your Review")}</h4>
                      <div className="mb-4">
                        <label className="mb-2 block text-sm font-medium">{t("carDetail.rating", "Rating")}</label>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                              className="focus:outline-none"
                            >
                              <Star className={`w-8 h-8 ${star <= newReview.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-300 hover:text-[#D4AF37]'} transition-colors`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="mb-2 block text-sm font-medium">{t("carDetail.comment", "Comment")}</label>
                        <Textarea
                          value={newReview.comment}
                          onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                          placeholder={t("carDetail.shareExperience", "Share your experience...")}
                          rows={4}
                          required
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" className="btn-gold text-black">{t("carDetail.submitReview", "Submit Review")}</Button>
                        {reviewSubmitting && <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />}
                        <Button type="button" variant="outline" onClick={() => setShowReviewForm(false)}>{t("common.cancel", "Cancel")}</Button>
                      </div>
                    </form>
                  )}

                  <div>
                    {reviewsLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                      </div>
                    ) : reviews.length > 0 ? (
                      reviews.map((review) => (
                        <ReviewCard
                          key={review.id}
                          review={review}
                          onMarkHelpful={handleReviewHelpful}
                          helpfulLoadingId={helpfulLoadingId}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">
                        {t("carDetail.noReviews", "No reviews yet. Be the first to share your experience.")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CarDetailPage;
